import React, { useState, useEffect, useRef, useContext } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import IcomoonReact from 'icomoon-react';
import styled from 'styled-components';
import moment from 'moment';

import iconSet from '../../assets/selection.json';
import { BRANDS } from '../../consts';

import { useAuthState } from '../../providers/authProvider';
import { UserDetailContext, UserDetailContextProps } from '../../context/userDetail/userDetailContext';

import {
  ButtonTerinary,
  ExpandableRow,
  ButtonPrimary,
  RowInputItem,
  NoResults,
  Loading,
  Table,
  GridTypeSelector,
  Icon,
} from '../../common/components';

import { StyledTitle, FilterRowWrapper } from '../../common/components';
import { GridFilter } from '../gridfilter/GridFilter';
import { getFuelIcon } from '../../utils/getFuelIcon';
import { EssAccountsTypes } from '../../types/account';
import { respondTo } from '../../common/styles/mixins';
import { globalApiParams } from '../../common/api/globals';
import { breakpoints } from '../../common/styles/variables';
import { isDraxBrand, isOpusBrand } from '../../utils/common';
import { TagManager } from '../utils/analytics/TagManager';
import * as colors from '../../common/styles/colors';

import { InvoicesWrapper, InvoicesGridWrapper } from './Invoices.styles';
import {
  downloadBulkInvoices,
  downloadInvoiceBackingData,
  downloadInvoices,
  getChargeDetailsForLedgerRevenueTransaction,
} from '../../common/api/invoicesApi';
import { InvoiceDownloadModal } from '../mymessages/modalTemplates/InvoiceDownloadModal';
import { InvoiceDataRow } from '../invoiceDataRow/InvoiceDataRow';
import * as variables from '../../common/styles/variables';
import { WarningInverse } from '../icons';
import { RequestReceivedModal } from '../mymessages/modalTemplates/RequestReceivedModal';
import { DataRequestedModal } from '../mymessages/modalTemplates/DataRequestedModal';
import { ContactUsModal } from '../mymessages/modalTemplates/ContactUsModal';
import { temporary } from '../../helpers/axiosWrapper';
import { NotificationBoxV2 } from '../health/NotificationBoxV2/NotificationBoxV2';

type InvoicesComponentProps = {
  hasElectricityAccount: boolean;
  hasGasAccount: boolean;
  hasRenewableAccount: boolean;
  initialType: string;
  gridData: Array<Api.IInvoiceDetail | Api.IRenewableStatement>;
  handleTypeChange: Function;
  handleSortChange: Function;
  handleSiteBlur: Function;
  handlePagingChange: any;
  handleCustomerFilterChange: Function;
  handleAccountFilterChange: Function;
  handleSiteFilterChange: Function;
  handleMpanMprnFilterChange: Function;
  handleDateFilterChange: Function;
  handlePeriodFilterChange: Function;
  handleClearAllClick: Function;
  handleDownloadReport: Function;
  handleDownloadBackingData: Function;
  handleDownloadSummary: Function;
  customerData: Array<Common.IOption>;
  accountData: Array<Common.IOption>;
  siteData: Array<Common.IOption>;
  mpanMprnData: Array<Common.IOption>;
  isLoading?: boolean;
  hasMoreData: boolean;
  currentSortField: string;
  currentSortOrder: 'ASC' | 'DESC' | '';
};

const defaultProps = {
  gridData: [],
  customerData: [],
  accountData: [],
  siteData: [],
  mpanMprnData: [],
  hasElectricityAccount: false,
  hasGasAccount: false,
  hasRenewableAccount: false,
  initialType: EssAccountsTypes.Electricity,
  isLoading: true,
  hasMoreData: true,
  currentSortField: '',
  currentSortOrder: 'ASC',
};

const invoicesHeaders: Array<Common.HeaderItem> = [
  {
    title: '',
    dataValue: 'icon',
  },
  {
    title: 'Invoice',
    dataValue: 'invoiceNumber',
  },
  {
    title: 'Account',
    dataValue: 'accountID',
  },
  {
    title: 'Period',
    dataValue: 'month',
  },
  {
    title: 'Site',
    dataValue: 'siteReference',
  },
  {
    title: 'Amount',
    dataValue: 'amount',
  },
];

const statementHeaders: Array<Common.HeaderItem> = [
  {
    title: '',
    dataValue: 'icon',
  },
  {
    title: 'Statement',
    sortField: 'statementId',
    dataValue: 'invoiceReference',
  },
  {
    title: 'Description',
    sortField: 'description',
    dataValue: 'invoiceName',
  },
  {
    title: 'Period',
    dataValue: 'month',
  },
  {
    title: 'Site',
    sortField: 'site',
    dataValue: 'customerSiteReference',
  },
  {
    title: 'Amount',
    sortField: 'amount',
    dataValue: 'invoiceValue',
  },
];

const draxInvoicesHeaders: Array<Common.HeaderItem> = [
  {
    title: '',
    dataValue: 'outstandingIcon',
    enableSort: false,
  },
  {
    title: 'Transaction date',
    dataValue: 'transactionDate',
    sortField: 'transaction-date',
    className: 'd-none d-md-table-cell',
    minWidth: 140,
    enableSort: false,
  },
  {
    title: 'Invoice',
    dataValue: 'invoiceNumber',
    sortField: 'transaction-reference',
    minWidth: 110,
    responsive: {
      [breakpoints.mobile]: 140,
    },
    enableSort: false,
  },
  {
    title: 'Site reference',
    dataValue: 'siteReference',
    sortField: 'site-reference',
    className: 'd-none d-md-table-cell',
    minWidth: 140,
  },
  {
    title: 'Site details',
    dataValue: 'siteAddress',
    className: 'd-none d-md-table-cell site-address-col',
    width: 10,
    enableSort: false,
  },
  {
    title: 'MPAN',
    dataValue: 'mpanElec',
    sortField: 'mpan-core',
    className: 'd-none d-md-table-cell',
    minWidth: 140,
  },
  {
    title: 'Period',
    dataValue: 'month',
    sortField: 'billing-period-end-date',
    className: 'd-none d-md-table-cell',
    minWidth: 140,
  },
  {
    title: 'Due date',
    dataValue: 'paymentDueFormatted',
    sortField: '',
    className: 'd-none d-md-table-cell',
    minWidth: 140,
    enableSort: false,
  },
  {
    title: 'Amount (£)',
    dataValue: 'amount',
    sortField: 'total',
    minWidth: 120,
    width: 120,
  },
];

const StyledTd = styled.td<any>`
  p,
  span {
    width: 50%;
  }

  p {
    display: flex;
    align-items: end;
    margin-top: auto;
  }

  ${respondTo.tablet`
    width: ${({ colWidth }: any) => colWidth};
    p, span {
      margin-left: -10px;
      width: auto;
    }

    p {
      display: block;
    }
  `};
`;

const InvoicesComponent = (props: InvoicesComponentProps) => {
  const authContext = useAuthState();
  const { userDetail } = useContext<UserDetailContextProps>(UserDetailContext);

  const [selectedRow, setSelectedRow] = useState<number | null>(null);
  const [selectedType, setSelectedType] = useState<string>(props.initialType);
  const [expandRow, setExpandRow] = useState<boolean>(false);
  const [chargeDetailsForLedgerRevenueTransaction, setChargeDetailsForLedgerRevenueTransaction] = useState<any>([]);

  const [gridTypes, setGridTypes] = useState<Array<string>>();
  const [isAllSelected, setSelectAll] = useState<boolean>(false);
  const [checkedItems, setCheckedItems] = useState<Array<number>>([]);
  const [downloadOptions, setDownloadOptions] = useState<Array<Common.IOption>>([]);
  const [isDownloadPdfRequestedModalOpened, setIsDownloadPdfRequestedModalOpened] = useState(false);
  const [isDownloadBackingDataRequestedModalOpened, setIsDownloadBackingDataRequestedModalOpened] = useState(false);
  const [isRequestReceivedModalOpened, setIsRequestReceivedModalOpened] = useState(false);
  const [isInvoiceDataRequestModalOpened, setIsInvoiceDataRequestModalOpened] = useState(false);
  const [isExpanding, setIsExpanding] = useState(false);
  const scrollableTable = useRef<any>();
  const [showContactUsModal, setShowContactUsModal] = useState<boolean>(false);

  const sortDetails: Common.ISort = { field: props.currentSortField, order: props.currentSortOrder };

  const scrollToTop = () => {
    if (scrollableTable.current) {
      scrollableTable.current.scrollTo(0, 0);
    }
  };

  const isTPIUser = (temporary.accessId && temporary.accessId.length > 0) || false;

  const paymentContactDetails = {
    contactEmail: 'collections@drax.com',
    contactNumber: '01473 707799',
  };

  useEffect(() => {
    if (props.gridData.length <= globalApiParams.pageSize) {
      scrollToTop();
    }
  }, [props.gridData]);

  useEffect(() => {
    const types = [];
    if (props.hasElectricityAccount) types.push(EssAccountsTypes.Electricity);
    if (props.hasGasAccount) types.push(EssAccountsTypes.Gas);
    if (props.hasRenewableAccount) types.push(EssAccountsTypes.Renewables);
    setGridTypes(types);
  }, [props.hasRenewableAccount, props.hasElectricityAccount, props.hasGasAccount]);

  useEffect(() => {
    setSelectedType(props.initialType);
  }, [props.initialType]);

  useEffect(() => {
    const invoiceOptions = [
      { value: 'invoice', display: 'Invoice' },
      { value: 'summary', display: 'Summary' },
    ];

    const statementOptions = [
      { value: 'statements', display: 'Statements' },
      { value: 'backingdata', display: 'Backing Data' },
    ];

    setDownloadOptions(selectedType === EssAccountsTypes.Renewables ? statementOptions : invoiceOptions);
  }, [selectedType]);

  const clearSelection = () => {
    setSelectedRow(null);
    setExpandRow(false);
    setSelectAll(false);
    setCheckedItems([]);
  };

  const handleClearAllClick = (): void => {
    props.handleClearAllClick();
  };

  const handleTypeChange = (type: string) => {
    setSelectedType(type);
    clearSelection();
    props.handleTypeChange(type, { field: 'billing-period-end-date', order: 'DESC' });
  };

  const handleMessageSelect = (event: any, row: number | null) => {
    const wrapper = event.target.closest('td')?.classList?.contains?.('select-item'); //!!
    if (wrapper) {
      return;
    }
    if (row !== undefined) {
      setSelectedRow(row);
      setExpandRow(false);
    }
  };

  const downloadStatement = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>, index: number) => {
    event.preventDefault();

    const essAdhocInvoiceIds: Array<number> = [];
    const item = props.gridData[index] as Api.IRenewableStatement;
    if (item.essAdhocInvoiceId) {
      essAdhocInvoiceIds.push(item.essAdhocInvoiceId);
      props.handleDownloadReport(essAdhocInvoiceIds);
    } else {
      console.log('Ess Adhoc Invoice ID is not valid');
    }
  };

  const downloadBackingData = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>, index: number) => {
    event.preventDefault();

    let essAdhocInvoiceIds: Array<number> = [];
    const item = props.gridData[index] as Api.IRenewableStatement;
    if (item.essAdhocInvoiceId) {
      essAdhocInvoiceIds.push(item.essAdhocInvoiceId);
      props.handleDownloadBackingData(essAdhocInvoiceIds);
    } else {
      console.log('Ess Adhoc Invoice ID is not valid');
    }
  };

  const toggleRow = async (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>, row: number | null) => {
    event.preventDefault();
    if (!expandRow) {
      TagManager.pushData({
        event: 'View Invoice Account info',
        customerAccountReference: userDetail.essCustomerId,
      });

      setIsExpanding(true);

      const expandedRow: any = props.gridData.find((_, index) => index === row);

      const transactionId = expandedRow.essInvoiceID;
      const page = 1;
      const pageSize = 100;
      const chargeDetails = await getChargeDetailsForLedgerRevenueTransaction(
        process.env.REACT_APP_API_URL,
        authContext,
        transactionId,
        page,
        pageSize
      );
      setChargeDetailsForLedgerRevenueTransaction(chargeDetails);

      setIsExpanding(false);
    }
    setExpandRow(!expandRow);
  };

  const handleItemChange = (index: number) => {
    setSelectedRow(null);

    const items = checkedItems.slice(0, checkedItems.length);

    const foundIndex = items.findIndex((checkedIndex: number) => checkedIndex === index);

    if (foundIndex > -1) {
      items.splice(foundIndex, 1);
    } else {
      items.push(index);
    }

    if (items.length === props.gridData.length) {
      handleSelectAll();
    } else if (isAllSelected) {
      setSelectAll(false);
    }

    setCheckedItems(items);
  };

  const handleSelectAll = () => {
    setSelectedRow(null);

    const select = !isAllSelected;
    setSelectAll(select);
    if (select) {
      const items: Array<number> = props.gridData.map((item, index) => index);
      setCheckedItems(items);
    } else {
      // unselect
      setCheckedItems([]);
    }
  };

  const handlePagingChange = () => {
    if (isAllSelected) {
      setSelectAll(!isAllSelected);
    }
    props.handlePagingChange();
  };

  const isChecked = (index: any) => {
    const foundIndex = checkedItems.findIndex((checkedIndex: number) => checkedIndex === index);
    return foundIndex > -1;
  };

  const onClickSort = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    e.preventDefault();
    const typeId = e.currentTarget.dataset.value || '';
    let details = sortDetails;
    if (details.field === typeId) {
      if (details.order === 'ASC') details.order = 'DESC';
      else details.order = 'ASC';
    } else {
      details.field = typeId;
      details.order = 'ASC';
    }
    props.handleSortChange(selectedType, details);
  };

  const getEssDocumentIds = () => {
    let essDocumentIds: Array<number> = [];
    checkedItems.map((index: number) => {
      const item = props.gridData[index] as Api.IInvoiceDetail;
      if (item.essDocumentID) {
        essDocumentIds.push(item.essDocumentID);
      }
      return item;
    });
    return essDocumentIds;
  };

  const getBackingData = () => {
    let invoices: Array<any> = [];

    checkedItems.map(index => {
      const item = props.gridData[index] as Api.IInvoiceDetail;
      const data = {
        transactionId: item.essDocumentID,
        mpan: item.mpanElec,
      };
      invoices.push(data);
    });
    return invoices;
  };

  const getEssAdhocInvoiceIds = () => {
    let essAdhocInvoiceIds: Array<number> = [];
    checkedItems.map((index: number) => {
      const item = props.gridData[index] as Api.IRenewableStatement;
      if (item.essAdhocInvoiceId) {
        essAdhocInvoiceIds.push(item.essAdhocInvoiceId);
      }
      return item;
    });
    return essAdhocInvoiceIds;
  };

  const getEssInvoiceIds = () => {
    let essInvoiceIds: Array<number> = [];
    checkedItems.map((index: number) => {
      const item = props.gridData[index] as Api.IInvoiceDetail;
      if (item.essInvoiceID) {
        essInvoiceIds.push(item.essInvoiceID);
      }
      return item;
    });
    return essInvoiceIds;
  };

  const handleDownload = (downloadType: string) => {
    if (downloadType === 'invoice') {
      const essDocumentIds = getEssDocumentIds();
      if (essDocumentIds.length === 0) {
        console.log('No Document IDs are valid');
        return;
      }
      props.handleDownloadReport(essDocumentIds);
    } else if (downloadType === 'summary') {
      // Electricity / Gas
      const essInvoiceIds = getEssInvoiceIds();
      if (essInvoiceIds.length === 0) {
        console.log('No Invoice IDs are valid');
        return;
      }
      props.handleDownloadSummary(essInvoiceIds);
    } else if (downloadType === 'statements') {
      // Renewables
      const essAdhocInvoiceIds = getEssAdhocInvoiceIds();
      if (essAdhocInvoiceIds.length === 0) {
        console.log('No Ess Adhoc Invoice IDs are valid');
        return;
      }
      props.handleDownloadReport(essAdhocInvoiceIds);
    } else if (downloadType === 'backingdata') {
      // Renewables
      const essAdhocInvoiceIds = getEssAdhocInvoiceIds();
      if (essAdhocInvoiceIds.length === 0) {
        console.log('No Ess Adhoc Invoice IDs are valid');
        return;
      }
      props.handleDownloadBackingData(essAdhocInvoiceIds);
    }
  };

  const getHeaderItems = () => {
    if (process.env.REACT_APP_BRAND === BRANDS.DRAX) {
      return draxInvoicesHeaders;
    }
    if (props.hasRenewableAccount && selectedType === EssAccountsTypes.Renewables) {
      return statementHeaders;
    }
    return invoicesHeaders;
  };

  const getActions = () => {
    if (process.env.REACT_APP_BRAND === BRANDS.DRAX) {
      return getInvoiceActions;
    }
    if (props.hasRenewableAccount && selectedType === EssAccountsTypes.Renewables) {
      return getStatementActions;
    }
    return getInvoiceActions;
  };

  const renderIcon = () => <td className='icon'>{getFuelIcon(selectedType)}</td>;

  const renderoutstandingIcon = (value: any) => {
    if (value) {
      return (
        <td className='outstandingIcon'>
          <div className='notification-badge'>X1</div>
        </td>
      );
    } else {
      return <td className='outstandingIcon'></td>;
    }
  };

  const renderPeriod = (value: number) => {
    if (isDraxBrand) {
      return <td className='d-none d-md-table-cell'>{value}</td>;
    }
    if (isOpusBrand) {
      return <td className='d-none d-md-table-cell'>{moment(value).format('MMM YY')}</td>;
    }
  };

  const renderCustomerSiteReference = (value: string, row: any) => {
    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      if (process.env.REACT_APP_BRAND === BRANDS.DRAX) {
        props.handleSiteBlur(row.siteID, e.target.value);
      } else {
        props.handleSiteBlur(row.portalSiteId, e.target.value);
      }
    };

    return (
      <td className='d-none d-md-table-cell'>
        {/* TODO: Check if <SiteRefField /> can we used in this case */}
        {/* TODO: API doesn't have: essSiteID, supplyActive, customerSiteReference*/}
        {/*<SiteRefField*/}
        {/*  siteId={row.essSiteID}*/}
        {/*  readOnly={!row.supplyActive}*/}
        {/*  siteReference={row.customerSiteReference}*/}
        {/*/>*/}

        <RowInputItem
          value={value || ''}
          placeholder='Edit'
          onHandleBlur={handleBlur}
          style={{ width: '150px' }}
          readOnly={row.siteEndDate && moment(row.siteEndDate).isBefore(new Date())}
        />
      </td>
    );
  };

  const renderSiteReference = (value: string, row: any) => {
    if (isDraxBrand) {
      return <></>;
    }

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => props.handleSiteBlur(row.siteID, e.target.value);
    return (
      <td className='d-none d-md-table-cell'>
        <RowInputItem
          value={value || ''}
          placeholder='Edit'
          onHandleBlur={handleBlur}
          style={{ width: '150px' }}
          readOnly={row.siteEndDate && moment(row.siteEndDate).isBefore(new Date())}
        />
      </td>
    );
  };

  const handleInvoiceBackingDataDownload = () => {
    if (checkedItems.length > 30) {
      handleBulkInvoiceBackingDataDownload();
    } else {
      const invoicesToDownload = {
        customerAccountReference: userDetail.essCustomerId,
        transactionChargeDetails: getBackingData(),
      };
      downloadInvoiceBackingData(process.env.REACT_APP_API_URL, invoicesToDownload, authContext);
    }
  };

  const handleBulkInvoiceBackingDataDownload = () => {
    const data: any = {
      customerAccountReference: userDetail.essCustomerId,
      invoiceDownloadFormat: 'XLSX',
      transactionChargeDetails: getBackingData(),
      endEmailaddresses: null,
    };
    downloadBulkInvoices(process.env.REACT_APP_API_URL, data, authContext);
  };

  const handleInvoicePdfDownload = () => {
    if (checkedItems.length > 30) {
      handleBulkInvoicePdfDownload();
    } else {
      const data: any = {
        'transaction-ids': getEssDocumentIds(),
      };
      downloadInvoices(process.env.REACT_APP_API_URL, data, authContext, userDetail.essCustomerId);
    }
  };

  const handleBulkInvoicePdfDownload = () => {
    const data: any = {
      customerAccountReference: userDetail.essCustomerId,
      invoiceDownloadFormat: 'ZIP',
      transactionChargeDetails: getBackingData(),
      endEmailaddresses: null,
    };
    downloadBulkInvoices(process.env.REACT_APP_API_URL, data, authContext);
  };

  const handleInvoicePdfClick = async (event: any) => {
    const downloadData = getEssDocumentIds();
    event.preventDefault();

    TagManager.pushData({
      event: 'Download invoice',
      count: downloadData.length,
      customerAccountReference: userDetail.essCustomerId,
    });

    setIsDownloadPdfRequestedModalOpened(true);
    handleInvoicePdfDownload();
  };

  const handleInvoiceBackingDataClick = async (event: any) => {
    const backingData = getBackingData();
    event.preventDefault();

    TagManager.pushData({
      event: 'Download invoice backing data',
      count: backingData.length,
      customerAccountReference: userDetail.essCustomerId,
    });

    setIsDownloadBackingDataRequestedModalOpened(true);
    handleInvoiceBackingDataDownload();
  };

  const handleInvoicePdfDownloadFromModal = () => {
    const downloadData = getEssDocumentIds();

    TagManager.pushData({
      event: 'Download invoice from modal',
      count: downloadData.length,
      customerAccountReference: userDetail.essCustomerId,
    });
  };

  const handleInvoiceBackingDataDownloadFromModal = () => {
    const backingData = getBackingData();

    TagManager.pushData({
      event: 'Download invoice backing data from modal',
      count: backingData.length,
      customerAccountReference: userDetail.essCustomerId,
    });
  };

  const renderAmount = (value: string, row: any, index: number) => (
    <td className={`${selectedRow === index ? 'd-none d-md-table-cell' : ''} amount-cell`}>
      {Number(value).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}
    </td>
  );
  const getInvoiceActions = (row: any, index: number) => (
    <div>
      <a href='/' onClick={e => toggleRow(e, index)} className='d-md-inline' style={{ marginLeft: '10px' }}>
        <IcomoonReact
          size={20}
          iconSet={iconSet}
          className='icon-chevron'
          color={expandRow && selectedRow === index ? colors.hydroBlue : colors.draxDarkBlue}
          icon={expandRow && selectedRow === index ? 'chevron-up-drax' : 'chevron-down-drax'}
        />
      </a>
    </div>
  );

  const getStatementActions = (row: any, index: number) => (
    <div>
      <div>
        <ButtonPrimary
          title='Statement'
          onClick={(event: any) => {
            downloadStatement(event, index);
          }}
          className='col m-0 d-block'
        />
      </div>
      <div>
        <ButtonTerinary
          title='Backing data'
          onClick={(event: any) => {
            downloadBackingData(event, index);
          }}
          className='col m-0 d-block'
        />
      </div>
    </div>
  );

  const getExpandableRow = (row: any, rowIndex: number) => (
    <ExpandableRow className={`sub-row ${selectedRow === rowIndex ? 'selected' : ''} ${expandRow ? 'open' : ''}`}>
      <div style={{ padding: '0 0', borderTop: '1px solid black' }}>
        <table style={{ width: '100%' }}>
          <tbody>
            <tr className='expandable-content'>
              <InvoiceDataRow data={chargeDetailsForLedgerRevenueTransaction}></InvoiceDataRow>
            </tr>
          </tbody>
        </table>
      </div>
    </ExpandableRow>
  );

  const title = props.hasRenewableAccount ? 'Invoices & Statements' : 'Invoices';
  const headers = getHeaderItems();
  const actions = getActions();

  const actionStyles = {
    width: '150px',
    opacity: '1',
  };

  const InvoiceFilterAdditionalItems = () => {
    const additionalItemsWarning = (
      <div className='d-block d-md-none warning-text mb-1'>
        *You can select a maximum of 900 invoices in a single request.
      </div>
    );

    const additionalItems = [
      <div
        className={`${checkedItems.length > 900 ? 'ml-4' : 'ml-auto'} mb-2 mb-sm-4 mt-3 position-relative add-button`}
      >
        <ButtonPrimary
          key='invoiceAdditionalItem1'
          title='Download invoice backing data'
          disabled={checkedItems.length <= 0 || checkedItems.length > 900}
          className={`${checkedItems.length > 900 ? 'ml-4' : 'ml-auto'} mb-2 mb-sm-4 mt-0 w-100`}
          onClick={handleInvoiceBackingDataClick}
        />
        <div className='badge badge-pill'>{checkedItems.length > 1 && checkedItems.length}</div>
      </div>,

      <div
        className={`${
          checkedItems.length > 900 ? 'ml-auto' : 'ml-auto ml-sm-4'
        } mb-2 mb-sm-4 mt-3 position-relative add-button`}
      >
        <ButtonPrimary
          key='invoiceAdditionalItem1'
          title='Download PDF invoices'
          disabled={checkedItems.length <= 0 || checkedItems.length > 900}
          className={`${checkedItems.length > 900 ? 'ml-4' : 'ml-auto'} mb-2 mb-sm-4 mt-0 w-100`}
          onClick={handleInvoicePdfClick}
        />
        <div className='badge badge-pill'>{checkedItems.length > 1 && checkedItems.length}</div>
      </div>,
    ];

    if (checkedItems.length > 900) {
      additionalItems.unshift(additionalItemsWarning);
    }

    return additionalItems;
  };

  const handleModalClose = () => {
    setIsRequestReceivedModalOpened(false);
    setIsInvoiceDataRequestModalOpened(false);
    clearSelection();
  };

  const handleCustomerFilterChange = (account: Common.IOption) => {
    clearSelection();
    props.handleCustomerFilterChange(account);
  };

  const handlePeriodFilterChange = (value: any) => {
    clearSelection();
    props.handlePeriodFilterChange(value);
  };

  const handleSiteFilterChange = (option: Common.IOption) => {
    clearSelection();
    props.handleSiteFilterChange(option);
  };

  const handleMpanMprnFilterChange = (option: Common.IOption) => {
    clearSelection();
    props.handleMpanMprnFilterChange(option);
  };

  const showOutstandingPaymentWarning = () => {
    const invoiceDetail = props.gridData as Api.IInvoiceDetail[];
    var show = invoiceDetail.findIndex(c => c.outstandingIcon === true) >= 0 ? true : false;
    return show;
  };

  const contactUsClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    setShowContactUsModal(true);
  };

  return (
    <InvoicesWrapper>
      <FilterRowWrapper>
        <StyledTitle>{title}</StyledTitle>
        <div className='help-text'>
          To download invoices*, select the corresponding checkbox and click the download button.
        </div>
      </FilterRowWrapper>
      <div className='mt-3 mb-4 page-text'>
        <p>
          Note: you can save time by scheduling automatic invoice reports. Head to the Scheduled reports page to choose
          the reports you want and the dates you want them. We’ll send you an email each month to let you know your
          reports are ready for downloading
        </p>
      </div>
      <GridFilter
        showSites={true}
        showDate={true}
        showPeriod={true}
        showMpanMprn={true}
        showClearAll={true}
        showOutstandingPaymentWarning={showOutstandingPaymentWarning()}
        siteData={props.siteData}
        showAccount={isOpusBrand}
        showDownload={isOpusBrand}
        selectedType={selectedType}
        accountData={props.accountData}
        handleDownload={handleDownload}
        customerData={props.customerData}
        mpanMprnData={props.mpanMprnData}
        downloadOptions={downloadOptions}
        handleClearAllClick={handleClearAllClick}
        enableDownloadBtn={checkedItems.length > 0}
        handleDateFilterChange={props.handleDateFilterChange}
        handlePeriodFilterChange={handlePeriodFilterChange}
        handleSiteFilterChange={handleSiteFilterChange}
        handleAccountFilterChange={props.handleAccountFilterChange}
        handleCustomerFilterChange={handleCustomerFilterChange}
        handleMpanMprnFilterChange={handleMpanMprnFilterChange}
        renderAdditionalListItems={InvoiceFilterAdditionalItems}
        handleOverdueBalanceWarning={contactUsClick}
      />

      {checkedItems.length > 900 && (
        <div className='d-none d-md-flex flex-row-reverse warning-text mt-0 mb-1'>
          *You can select a maximum of 900 invoices in a single request.
        </div>
      )}

      <div className='d-md-none'>
        {showOutstandingPaymentWarning() && (
          <NotificationBoxV2
            type='X1'
            boxText={'This invoice is overdue...'}
            showButton={true}
            buttonText='contact us'
            onClick={contactUsClick}
          ></NotificationBoxV2>
        )}
      </div>

      {isRequestReceivedModalOpened && (
        <InvoiceDownloadModal
          show={isRequestReceivedModalOpened}
          onClose={handleModalClose}
          invoiceCount={checkedItems.length}
        />
      )}

      <DataRequestedModal
        show={isInvoiceDataRequestModalOpened}
        onHide={() => {
          handleModalClose();
        }}
        dataType='Invoice'
      />

      {isDownloadPdfRequestedModalOpened && (
        <RequestReceivedModal
          show={isDownloadPdfRequestedModalOpened}
          onYes={() => {
            handleInvoiceBackingDataDownload();
            setIsDownloadPdfRequestedModalOpened(false);
            handleInvoiceBackingDataDownloadFromModal();
            if (checkedItems.length > 30) {
              setIsInvoiceDataRequestModalOpened(true);
            } else {
              setIsRequestReceivedModalOpened(true);
            }
          }}
          onNo={() => {
            setIsDownloadPdfRequestedModalOpened(false);
            if (checkedItems.length > 30) {
              setIsInvoiceDataRequestModalOpened(true);
            } else {
              setIsRequestReceivedModalOpened(true);
            }
          }}
          bodyTopText={'The PDF invoices file will start downloading shortly.'}
          bodybottomText={'Would you like to download invoice backing data for the same selection?'}
        />
      )}

      {isDownloadBackingDataRequestedModalOpened && (
        <RequestReceivedModal
          show={isDownloadBackingDataRequestedModalOpened}
          onYes={() => {
            handleInvoicePdfDownload();
            setIsDownloadBackingDataRequestedModalOpened(false);
            handleInvoicePdfDownloadFromModal();
            if (checkedItems.length > 30) {
              setIsInvoiceDataRequestModalOpened(true);
            } else {
              setIsRequestReceivedModalOpened(true);
            }
          }}
          onNo={() => {
            setIsDownloadBackingDataRequestedModalOpened(false);
            if (checkedItems.length > 30) {
              setIsInvoiceDataRequestModalOpened(true);
            } else {
              setIsRequestReceivedModalOpened(true);
            }
          }}
          bodyTopText={'The invoice backing data file will start downloading shortly.'}
          bodybottomText={'Would you like to download the invoice PDF data for the same selection?'}
        />
      )}

      <GridTypeSelector initialType={selectedType} types={gridTypes} handleTypeChange={handleTypeChange} />

      {!props.isLoading && props.gridData.length === 0 && <NoResults />}

      <InvoicesGridWrapper>
        {props.gridData.length > 0 && (
          <>
            <InfiniteScroll
              dataLength={props.gridData.length}
              next={handlePagingChange}
              hasMore={props.hasMoreData}
              loader={''}
              scrollableTarget='scrollableDiv'
              scrollThreshold={0.9}
            >
              <div
                id='scrollableDiv'
                ref={scrollableTable}
                className='table-responsive table-fixed fixed-column columns-1'
              >
                <Table
                  isSelectable={true}
                  headers={headers}
                  actions={actions}
                  rows={props.gridData}
                  isChecked={isChecked}
                  sortDetails={sortDetails}
                  onClickSort={onClickSort}
                  isRowExpanded={expandRow}
                  actionStyles={actionStyles}
                  isAllSelected={isAllSelected}
                  selectedRowIndex={selectedRow}
                  expandableRow={getExpandableRow}
                  handleSelectAll={handleSelectAll}
                  handleCheckRow={handleItemChange}
                  handleSelectRow={handleMessageSelect}
                  className='list table plain text-left'
                  rendericon={renderIcon}
                  rendermonth={renderPeriod}
                  renderamount={renderAmount}
                  rendergrossAmount={renderAmount}
                  renderSiteReference={renderSiteReference}
                  rendercustomerSiteReference={renderCustomerSiteReference}
                  renderoutstandingIcon={renderoutstandingIcon}
                />
              </div>
            </InfiniteScroll>
          </>
        )}

        {(props.isLoading || isExpanding) && <Loading overlay nowait />}
      </InvoicesGridWrapper>

      <ContactUsModal
        show={showContactUsModal}
        onHide={() => {
          setShowContactUsModal(false);
        }}
        contactEmail={paymentContactDetails.contactEmail}
        contactNumber={paymentContactDetails.contactNumber}
        isDirectCustomer={!isTPIUser}
        isTpiCustomer={isTPIUser}
      ></ContactUsModal>
    </InvoicesWrapper>
  );
};

InvoicesComponent.defaultProps = defaultProps;

export default InvoicesComponent;
